import { ReactNode } from 'react';
import clsx from 'clsx';
import styles from './Table.module.css';
import { AdjustProps } from '../Adjust/Adjust';
import adjustStyles from '../Adjust/Adjust.module.css';
import { PersonalText } from '../PersonalText/PersonalText';

export enum TableStyle {
    DEFAULT = '',
    TYPE2 = 'var-02',
    TYPE3 = 'var-03',
}

export enum ResponsiveType {
    NONE = '',
    SCROLL = 'of-scroll',
    BREAK = 'of-break',
}

export interface TableCellProps {
    id?: string;
    isCellElement?: boolean;
    colSpan?: number;
    rowSpan?: number;
    children?: ReactNode;
    onClick?: (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => void;
}

export interface TableRowProps {
    isFirstColNotHeader?: boolean;
    items?: TableCellProps[];
    children?: ReactNode;
    onClick?: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
}

export interface TableAdjusts {
    adjustMb?: Pick<AdjustProps, 'mb'>;
}

export interface ColumnStyle {
    adjust?: Pick<AdjustProps, 'width'>;
}

export interface TableProps {
    title?: string;
    columnsStyle?: ColumnStyle[];
    header?: TableRowProps;
    rows?: TableRowProps[];
    isFirstColNotHeader?: boolean;
    onClick?: (event: React.MouseEvent<HTMLTableElement, MouseEvent>) => void;
    responsiveType?: ResponsiveType;
    style?: TableStyle;
    adjusts?: TableAdjusts;
    children?: ReactNode;
}

/**
 * 個人情報が含まれるテーブルセル
 */
export const PersonalTableCell = (props: TableCellProps) => {
    const {id, colSpan, rowSpan, children, onClick: handleClick} = props;

    return (
        <td id={id} rowSpan={rowSpan} colSpan={colSpan} onClick={handleClick}>
            <PersonalText>{children}</PersonalText>
        </td>
    );
};

export const TableCell = (props: TableCellProps) => {
    const {id, colSpan, rowSpan, children, onClick: handleClick} = props;

    return (
        <td id={id} rowSpan={rowSpan} colSpan={colSpan} onClick={handleClick}>
            {children}
        </td>
    );
};

export const TableHeader = (props: TableCellProps) => {
    const {id, colSpan, rowSpan, children, onClick: handleClick} = props;

    return (
        <th id={id} rowSpan={rowSpan} colSpan={colSpan} onClick={handleClick}>
            {children}
        </th>
    );
};

export const TableRow = (props: TableRowProps) => {
    const {items, isFirstColNotHeader, children, onClick: handleClick} = props;

    return (
        <tr onClick={handleClick}>
            {items &&
                items.map((cell: TableCellProps, index: number) => {
                    if (index === 0 && !isFirstColNotHeader && !cell.isCellElement) {
                        return <TableHeader {...cell} key={index} />;
                    }

                    return <TableCell {...cell} key={index} />;
                })}
            {children}
        </tr>
    );
};

const TableHeaders = (props: TableRowProps) => {
    const {items, onClick: handleClick} = props;

    return (
        <thead>
            <tr onClick={handleClick}>
                {items && items.map((data: TableCellProps, index: number) => <TableHeader {...data} key={index} />)}
            </tr>
        </thead>
    );
};

export const Table = (props: TableProps) => {
    const {
        title,
        header,
        columnsStyle,
        rows,
        onClick: handleClick,
        responsiveType = ResponsiveType.NONE,
        style = TableStyle.DEFAULT,
        isFirstColNotHeader = false,
        adjusts,
        children,
    } = props;
    const marginBottom = adjusts?.adjustMb?.mb && adjustStyles[`mb-${adjusts.adjustMb.mb}`];
    const componentStyles = clsx(styles['mod-tbl'], styles[style], styles[responsiveType], marginBottom);

    return (
        <div
            className={componentStyles}
            onClick={handleClick}
            tabIndex={responsiveType === ResponsiveType.SCROLL ? 0 : undefined}>
            <table>
                {title && <caption>{title}</caption>}
                {header && <TableHeaders {...header} />}
                {columnsStyle && columnsStyle.length > 0 && (
                    <colgroup>
                        {columnsStyle.map((style, index: number) => {
                            const componentStyles =
                                style.adjust && style.adjust.width && adjustStyles[`w-${style.adjust.width}`];

                            return <col key={index} className={componentStyles} />;
                        })}
                    </colgroup>
                )}
                {rows && rows.length > 0 ? (
                    <tbody>
                        {rows.map((row: TableRowProps, index: number) => (
                            <TableRow {...row} isFirstColNotHeader={isFirstColNotHeader} key={index} />
                        ))}
                    </tbody>
                ) : (
                    children && <tbody>{children}</tbody>
                )}
            </table>
        </div>
    );
};
